<template>
    <!-- Modal: useDialog moves focus in, keeps Tab inside, closes on Escape
         and hands focus back to the button that opened it. -->
    <section ref="dialogEl" class="add-to-itinerary" role="dialog" aria-modal="true" aria-labelledby="add-to-itinerary-title" tabindex="-1">
        <div class="inner-wrapper container">
            <header>
                <!-- Was labelled "Close Filters", copied from the filter panel. -->
                <button type="button" class="back-caret" @click.prevent="close" aria-label="Close">
                    <SvgsBackCaret />
                </button>
                <h2 id="add-to-itinerary-title" class="subhead-2">Add Site To My Itinerary</h2>
            </header>
            <article class="card-site">
                <figure class="image-wrapper">
                    <!-- A fixed 70px thumbnail, so an explicit width is right here: nuxt-img
                         still emits a 2x candidate for it. It was fetching 300px. -->
                    <mess-image :src="photo" placeholder loading="lazy" :width="70" />
                </figure>
                <div class="content">
                    <div class="copy">
                        <p class="gray-tag">{{ neighborhoodName }}</p>
                        <h3 class="headline-2">{{ item.fields['Site'] }}</h3>
                        <p v-if="item && item.fields && item.fields['Site Address']" class="address body-small">
                            {{ item.fields['Site Address'] }}
                        </p>
                    </div>
                </div>
            </article>

            <section class="add-site-to-itinerary">
                <h2 class="headline-2">Select an Itinerary to Add This Site To</h2>
                <ul class="itinerary-list">
                    <li v-for="(itinerary, itineraryIndex) in itineraries" :key="itinerary.name" class="item">
                        <CardsItinerary :itinerary="itinerary" @itinerarySelected="handleSelection" />
                        <div v-if="itineraryIndex === isAlreadyInItinerary" class="already-in-itinerary">
                            <h3 class="card-header">Site is already on this itinerary</h3>
                        </div>
                    </li>
                </ul>

                <!-- The two actions are siblings so they can share a row where
                     there is width for it (Asana O2-66). The "new itinerary"
                     button used to be the last <li> of the itinerary list, which
                     also read oddly: a button announced as one of the itineraries
                     to choose from. -->
                <div class="modal-actions">
                    <button @click.prevent="openNewItineraryModal" :disabled="!isOnline" :title="!isOnline ? 'Unavailable offline' : undefined" class="button black add-site">
                        <SvgsAdd />
                        Add Site To New Itinerary
                    </button>

                    <button @click.prevent="close" class="button cancel-button">Cancel</button>
                </div>
            </section>

            <div v-if="addSiteToNewItineraryModalIsOpen" class="modal-inner-container container">
                <GlobalCreateItineraryWithSite @closeFlyout="closeNewItineraryModal" :site="item" />
            </div>
        </div>
    </section>
</template>

<script setup>
    // Offline read-only — the store refuses these writes; the control must
    // not look live. See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
    const { isOnline } = useOnlineStatus();
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();
    
    // Composables
    const { getImageFromArray } = useImageUrl();

    // TO BE REMOVED
    const { getOhcList } = useUtils();

    // Props
    const props = defineProps({
        item: {
            type: Object,
            required: false,
            default: {},
        },
    });
    const { item } = toRefs(props);

    // Variables
    const { $event } = useNuxtApp();
    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;
    const { getSiteNeighborhoodName } = useUtils();
    const { itineraries } = storeToRefs(userStore);

    onMounted(() => {
        if (import.meta.client) {
            const { ohcList } = getOhcList('ohcItineraries');
            // itineraries.value = ohcList;
        }
    });

    function close() {
        if (addSiteToNewItineraryModalIsOpen.value) {
            return closeNewItineraryModal();
        } else {
            return $event('closeGlobalItineraryModal');
        }
    }

    // Escape steps back the same way the back caret does: out of "new
    // itinerary" first, then out of the modal.
    const dialogEl = ref(null);
    useDialog(dialogEl, { onEscape: close });

    function addedToItineraryAlert() {
        $event('addedToItineraryAlert', {
            item: item.value,
            selected: selectedItinerary.value,
        });
    }

    const photo = computed(() => {
        if (item.value?.fields?.['Selected Photos']) {
            return getImageFromArray(item.value.fields['Selected Photos'], 0);
        }
        return '';
    });

    const neighborhoodName = getSiteNeighborhoodName(item, neighborhoodsMap);

    function refreshItineraries() {
        const { ohcList } = getOhcList('ohcItineraries');
        // itineraries.value = ohcList;
    }

    const isAlreadyInItinerary = ref(null);
    const selectedItinerary = ref(null);

    function handleSelection(val) {
        if (!val.name) {
            console.error('No name provided for itinerary');
        } else {
            const add = userStore.addSiteToItinerary(val.name, item.value.id);
            if (add?.error) {
                const itinerariesIndex = itineraries.value.findIndex((itinerary) => itinerary.name === val.name);
                isAlreadyInItinerary.value = itinerariesIndex;
            } else {
                selectedItinerary.value = val;
                addedToItineraryAlert();
                close();
            }
        }
        let { ohcList, clone } = getOhcList('ohcItineraries');
        if (!ohcList?.length) return;
    }

    const addSiteToNewItineraryModalIsOpen = ref(false);
    function openNewItineraryModal() {
        addSiteToNewItineraryModalIsOpen.value = true;
    }
    function closeNewItineraryModal() {
        addSiteToNewItineraryModalIsOpen.value = false;
    }
</script>

<style lang="scss" scoped>
    .add-to-itinerary {
        z-index: $zStepOne;
        overflow: auto;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba($gray-darkest, 0.7);

        // Focus lands on the dialog itself when it opens.
        &:focus {
            outline: none;
        }

        .inner-wrapper {
            overflow: auto;
            height: calc(100svh - 59px);
            margin-top: 59px;
            padding: 16px;
            // Runs to the bottom of the screen on a phone, so Cancel needs to
            // clear the iPhone home indicator.
            padding-bottom: calc(16px + env(safe-area-inset-bottom));
            background-color: #ffffff;
        }

        header {
            position: relative;
            text-align: center;
            margin-bottom: 34px;

            .reset {
                position: absolute;
                right: 0;
                top: 2px;
            }

            .back-caret {
                position: absolute;
                left: 0;
                top: -3px;
            }
        }

        .card-site {
            display: flex;
            margin-bottom: 30px;
            padding: 20px 15px;
            background-color: $gray-light;

            .image-wrapper {
                display: block;
                position: relative;
                width: 70px;
                height: 70px;
                flex-shrink: 0;
                margin-right: 17px;
                margin-bottom: 0.5em;
            }

            img {
                position: absolute;
                inset: 0 0 0 0;
                height: 100%;
                width: 100%;
                object-fit: cover;
                object-position: center;
            }

            .gray-tag {
                font-size: 8px;
                margin-top: 0;
                padding: 0;
                background-color: transparent;
                margin-bottom: 6px;
            }

            .footer {
                display: flex;
                margin-top: 10px;

                button {
                    margin-right: 15px;
                }
            }

            h3 {
                margin-bottom: 10px;
            }
        }

        .itinerary-list {
            margin: 0; padding: 0;
            margin-top: 20px;

            .item {
                position: relative;
                margin-bottom: 15px;
                border: 1px solid #9d9fa2;

                .already-in-itinerary {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(69, 71, 72, 0.9);
                    color: $white;
                    text-align: center;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
            }

        }

        // Stacked by default, side by side once there is room (Asana O2-66).
        //
        // GRID rather than flex: `flex: 1 1 0` on both buttons still sized them
        // 431 and 483 here, because these two carry different padding (10px
        // against 18px/36px) and flex resolves that through content-based sizing
        // even with min-width:0. `1fr 1fr` is defined as equal tracks, so the
        // two match regardless of what is inside them.
        .modal-actions {
            display: grid;
            gap: 15px;
            margin-top: 40px;

            .button {
                margin: 0;
            }

            @media (min-width: $tablet) {
                grid-template-columns: 1fr 1fr;
                gap: 16px;
            }
        }

        .modal-inner-container {
            z-index: $zStepTwo;
            position: absolute;
            top: 59px;
            right: 0;
            bottom: 0;
            left: 0;
            padding: 16px;
            background-color: $white;
        }



        @media (min-width: $tablet) {
            display: flex;
            justify-content: center;
            align-items: center;

            .inner-wrapper.container {
                padding: 16px;
                width: 70%;
                max-width: $hd;
                margin: auto;
                height: 80%;
                top: 0px;
                bottom: 0px;
                left: 0;
                right: 0;
                overflow-x: auto;
                overflow-y: auto;

                // Bottom padding was 0, so the last control — Cancel — sat
                // flush against the bottom edge of this scrolling box and read
                // as cut off once the content was tall enough to overflow, which
                // it is as soon as you have an itinerary (Asana O2-66).
                @media (min-width: $tablet) {
                    padding: 64px;
                }
            }

            .modal-inner-container.container {
                padding: 16px;
                width: 70%;
                max-width: $hd;
                margin: auto;
                height: 80%;
                top: 0px;
                bottom: 0px;
                left: 0;
                right: 0;
                overflow-x: auto;
                overflow-y: auto;
            }

        }
    }
</style>
